import { ValidValueProps, useValidationFeedback } from '@n2-common/components/ValidationFeedbackFormik';
import { FieldHelperProps, useField } from 'formik';
import { type ChangeEvent, type KeyboardEvent, useCallback, useRef, useMemo } from 'react';
import type { BaseFieldProps, UiStateProps } from '.';
import { ErrorMessage } from '../Container';
import { useHelpBox } from '../HelpBox';
import { joinNames } from '../helpers';
import Label from '../Label';
import { NotificationIcon } from '../NotificationIcon';
import { maskTrackingAttributes } from './TextInput';
import style from './textInput.module.scss';
import useDefaultId from '@n2-common/lib/useDefaultId';

export type FormattedInputConfig = {
    /** Function that returns the desired visible output */
    format: (value?: string | number | readonly string[]) => string;
    /** Function that normalizes the input (formatted). Here you remove f.ex. whitespaces */
    cleanup: (value: string) => string;
    /** InputMode is a hint to useragen, which keyboard to bring up for input. */
    inputMode?: React.InputHTMLAttributes<HTMLInputElement>['inputMode'];
    prefix?: string;
    postfix?: string;
    /** If set, the shadowfield is rendered. */
    debug?: true;
};

export type FormattedInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'prefix'> & BaseFieldProps & FormattedInputConfig;

export default function FormattedInput({
    id,
    name,
    label,
    error,
    uistate,
    tabIndex,
    helptext,
    className: outerClassName,
    helpers,
    onChange,
    onBlur,
    onInput,
    value,
    format,
    cleanup,
    inputMode = 'numeric',
    prefix,
    postfix,
    debug,
    ...attr
}: FormattedInputProps & UiStateProps & { helpers?: FieldHelperProps<unknown> }) {
    const errorId = `${id}error`;
    tabIndex = tabIndex || 0;
    const [HelpBox, HelpBoxIcon] = useHelpBox(helptext, name);
    const shadowRef = useRef<HTMLInputElement>(null);

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const shadow = shadowRef.current;
            if (shadow) {
                if (helpers) {
                    helpers.setValue(cleanup(e.currentTarget.value), true);
                } else {
                    shadow.value = cleanup(e.currentTarget.value);
                    shadow.dispatchEvent(new Event('change'));
                }
            }
        },
        [cleanup, helpers]
    );

    const handleBlur = useCallback(() => {
        helpers?.setTouched(true);
    }, [helpers]);

    const handleInput = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const event = { target: { value: cleanup(e.currentTarget.value), nodeName: 'INPUT' } };
            onInput && onInput(event as ChangeEvent<HTMLInputElement>);
        },
        [onInput, cleanup]
    );

    const handleKeyUp = useCallback(
        function handleKeyUp(e: KeyboardEvent<HTMLInputElement>) {
            // We only want to reformat when pressed key is changing value - not eg. arrows etc.
            if (/^(.{1}|Backspace|Delete)$/.test(e.key)) {
                const value = e.currentTarget.value;
                const formatted = format(value);
                const position = findPosition(e.currentTarget.selectionStart, value, formatted);

                e.currentTarget.value = formatted;
                e.currentTarget.selectionStart = position;
                e.currentTarget.selectionEnd = position;
            }
        },
        [format]
    );

    return (
        <div className={joinNames(style.root, outerClassName)}>
            <div className={style.labelWrapper}>
                <Label htmlFor={id}>{label}</Label>
                <HelpBoxIcon />
            </div>
            <HelpBox />
            <div className={joinNames(style.wrapper, uistate == 'value-verified' && style.valueVerified, error ? style.error : undefined)}>
                {prefix && <div className={style.prefix}>{prefix}</div>}
                <input
                    id={id}
                    name={`${name}-shadow`}
                    tabIndex={tabIndex}
                    defaultValue={format(value)}
                    type="text"
                    inputMode={inputMode}
                    onChange={handleChange}
                    onKeyUp={handleKeyUp}
                    onInput={handleInput}
                    onBlur={handleBlur}
                    {...attr}
                    aria-errormessage={errorId}
                    aria-invalid={error ? true : undefined}
                    {...maskTrackingAttributes}
                />
                {postfix && <div className={style.postfix}>{postfix}</div>}
                <NotificationIcon prefixForUiTesting={name} check={uistate == 'value-verified'} alert={!!error && { title: error }} />
            </div>
            <input
                style={{ display: debug ? 'block' : 'none', border: '1px dashed red' }}
                tabIndex={-1}
                name={name}
                type={inputMode}
                ref={shadowRef}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                {...maskTrackingAttributes}
            />
            {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
        </div>
    );
}

type FormikFormattedInputProps = Omit<FormattedInputProps, 'uiType'> & ValidValueProps;
export function FormikFormattedInput({ name, defaultValue, inputMode, id, onValidValue, ...attr }: FormikFormattedInputProps): JSX.Element {
    const compId = useDefaultId(id);
    const [{ onChange, onBlur, value }, { touched, error }, helpers] = useField({
        name,
        type: inputMode,
        value: defaultValue,
    });
    const [verified, onInput] = useValidationFeedback(name, onValidValue);
    const uistate = useMemo(() => (verified ? 'value-verified' : 'standard'), [verified]);
    return (
        <FormattedInput
            id={compId}
            uistate={uistate}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            onInput={onInput}
            error={touched && error}
            name={name}
            helpers={helpers}
            inputMode={inputMode}
            {...attr}
        />
    );
}

/**
 * Helper to look around for cursorposition when formatting have changed
 * @param position
 * @param value
 * @param formatted
 * @returns
 */
function findPosition(position: number | null, value: string, formatted: string) {
    if (position === null) {
        return position;
    }

    if (formatted.length != value.length) {
        // Format changed content
        if (position == value.length) {
            // Cursor at end
            //console.log('At end');
            return formatted.length;
        }
    }

    const left = value[position - 1];
    if (left != formatted[position - 1]) {
        // look right
        if (left == formatted[position]) {
            return position + 1;
        }
        // go left
        return position - 1;
    }

    return position;
}
